import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload, faFloppyDisk, faPencil, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { PAGE_PATHS } from './base_report';
import Button from 'shared/button';
import Dropdown from 'components/dropdown';
import PropTypes from 'prop-types';

export const GENERATING_CSV_STEPS = {
  DEFAULT: 'default',
  GENERATING: 'generating',
  GENERATED: 'generated',
};

export default function ReportHeaderActions({
  allSelected,
  csvDownloadPath,
  disablePdf = false,
  downloadKey,
  generatingCSVStep,
  ransackParams,
  savedReport,
  selectedColumns,
  selectedRecordIds,
  onEditColumns,
  onPathChanged,
  onSaveAs,
  onUpdateReport
}) {
  const csvReturnFields = () => selectedColumns.map(column => column.label);

  function createCsvPath() {
    const url = new URL(csvDownloadPath, window.location.origin);
    if (allSelected || selectedRecordIds.length === 0) {
      Object.entries(ransackParams()).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((individualValue) => {
            url.searchParams.append(`${key}[]`, individualValue);
          });
        } else {
          url.searchParams.append(key, value);
        }
      });
    } else {
      selectedRecordIds.forEach(recordId => url.searchParams.append('ids[]', recordId));
    }
    csvReturnFields().forEach(field => url.searchParams.append('columns[]', field));
    url.searchParams.append('key', downloadKey.current);
    return url.toString();
  }

  const renderSaveDropdownActions = () => {
    if (!savedReport) {
      return (
        <div>
          <Button.Primary
            icon={faFloppyDisk}
            label="Save As"
            onClick={onSaveAs}
          />
        </div>
      );
    } else {
      return (
        <Dropdown controlIcon={faFloppyDisk} controlLabel="Save" id="save-dropdown" variant="primary">
          <Dropdown.MenuItem
            key="save-as-report"
            name="save-as-report"
            onClick={onSaveAs}
          >
            Save As
          </Dropdown.MenuItem>
          <Dropdown.MenuItem
            key="save-report"
            onClick={onUpdateReport}
          >
            Save
          </Dropdown.MenuItem>
        </Dropdown>
      );
    }
  };

  return (
    <div className="d-flex gap-3">
      {generatingCSVStep === GENERATING_CSV_STEPS.GENERATING && (
        <div className="text-primary d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faSpinner} spin/>
          Generating CSV...
        </div>
      )}
      {generatingCSVStep === GENERATING_CSV_STEPS.GENERATED && (
        <div className="text-success d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCheck}/>
          Generated CSV
        </div>
      )}
      <Button.Cancel
        className="px-2"
        disabled={generatingCSVStep !== GENERATING_CSV_STEPS.DEFAULT}
        href={generatingCSVStep === GENERATING_CSV_STEPS.DEFAULT ? createCsvPath() : ''}
        plain
        onClick={
          () => generatingCSVStep === GENERATING_CSV_STEPS.DEFAULT ? onPathChanged(PAGE_PATHS.GENERATE_CSV) : {}
        }
      >
        <FontAwesomeIcon icon={faDownload} size="xl"/>
      </Button.Cancel>
      <Dropdown controlLabel="Actions" id="actions-dropdown">
        {!disablePdf && (
          <Dropdown.MenuItem
            disabled={selectedRecordIds.length === 0}
            key="generate-pdf"
            onClick={() => onPathChanged(PAGE_PATHS.GENERATE_PDF)}
          >
            <FontAwesomeIcon className="mr-2" fixedWidth icon={faDownload}/>
            Download PDF
          </Dropdown.MenuItem>
        )}
        <Dropdown.MenuItem key="edit-columns" onClick={onEditColumns}>
          <FontAwesomeIcon className="mr-2" fixedWidth icon={faPencil}/>
          Edit Columns
        </Dropdown.MenuItem>
      </Dropdown>
      {renderSaveDropdownActions()}
    </div>
  );
}

ReportHeaderActions.propTypes = {
  allSelected: PropTypes.bool.isRequired,
  csvDownloadPath: PropTypes.string.isRequired,
  disablePdf: PropTypes.bool,
  downloadKey: PropTypes.object.isRequired,
  generatingCSVStep: PropTypes.oneOf(Object.values(GENERATING_CSV_STEPS)).isRequired,
  ransackParams: PropTypes.func.isRequired,
  savedReport: PropTypes.object,
  selectedColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRecordIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onEditColumns: PropTypes.func.isRequired,
  onPathChanged: PropTypes.func.isRequired,
  onSaveAs: PropTypes.func.isRequired,
  onUpdateReport: PropTypes.func.isRequired,
};
